import { useMemo, useState } from "react";
import Hub from "../../api/Hub";
import { getUser } from "../../core/Global";
import Icon from "../base/Icon";
import Avatar from "../base/Avatar";
import Indicator from "../base/Indicator";
import FlexRow from "../base/FlexRow";
import { BottomSheet } from "../base/BottomSheet";
import "./FilteringMobile.css";


// Mobile counterpart of <Filtering />. Instead of one search box, each JSON filter is a
// row/select; tapping it opens an iOS bottom sheet to pick value(s) (multi-select for text,
// Yes/No for boolean). Produces the SAME query string the desktop Filtering does
// (`name=value` repeated per selected value) so the backend gets identical filters.

export interface FilterSelItem {
    value: string
    text?: string
    subText?: string
    icon?: any
    color?: string
    imageUrl?: string
}

// name -> selected values
export type FilterSelections = Record<string, FilterSelItem[]>

export function filterSelectionsToQuery(sel: FilterSelections): string {
    let q = "";
    Object.keys(sel).forEach(name => {
        (sel[name] ?? []).forEach(it => {
            if (q) q += "&";
            q += name + "=" + encodeURIComponent(it.value ?? "");
        });
    });
    return q;
}

interface IProps {
    apiRoute?: string
    filteringProps?: any[]
    value: FilterSelections
    onChange: (value: FilterSelections) => void
}

export default function FilteringMobile(props: IProps) {

    const user = getUser();
    const hub = useMemo(() => new Hub<any>(props.apiRoute), [props.apiRoute]);

    const [activeProp, activePropSet] = useState<any>();
    const [values, valuesSet] = useState<FilterSelItem[]>([]);
    const [loading, loadingSet] = useState<boolean>(false);
    const [keyword, keywordSet] = useState<string>("");

    const items = (props.filteringProps ?? []).filter(p => !(p.systemAdmin && !user?.isSuperAdmin));

    const selected = (name?: string): FilterSelItem[] => (name ? props.value[name] ?? [] : []);

    const isSelected = (name: string, val: string) => selected(name).some(s => s.value === val);

    const summary = (p: any): string => {
        const sel = selected(p.name);
        if (sel.length === 0) return "Any";
        if (sel.length === 1) return sel[0].text ?? sel[0].value;
        return sel.length + " selected";
    };

    const fetchValues = (p: any) => {
        loadingSet(true);
        // Scope values by the OTHER selected filters (like the desktop routeParams), so e.g.
        // choosing an agent narrows the technicians — but never exclude this filter's own options.
        const params: { name: string; value: string }[] = [];
        Object.keys(props.value).forEach(n => {
            if (n === p.name) return;
            (props.value[n] ?? []).forEach(it => params.push({ name: n, value: it.value }));
        });
        hub.GetFilterValues({ name: p.name, keyword: "" }, params, res => {
            valuesSet((res?.data as FilterSelItem[]) ?? []);
            loadingSet(false);
        });
    };

    const openFilter = (p: any) => {
        keywordSet("");
        activePropSet(p);
        if (p.type === "boolean") {
            valuesSet([{ value: "True", text: "Yes" }, { value: "False", text: "No" }]);
            loadingSet(false);
        } else {
            valuesSet([]);
            fetchValues(p);
        }
    };

    const toggleValue = (p: any, item: FilterSelItem) => {
        const cur = selected(p.name);
        let next: FilterSelItem[];
        if (p.type === "boolean") {
            next = isSelected(p.name, item.value) ? [] : [item];
        } else {
            next = isSelected(p.name, item.value)
                ? cur.filter(s => s.value !== item.value)
                : [...cur, item];
        }
        const nextVal: FilterSelections = { ...props.value, [p.name]: next };
        if (next.length === 0) delete nextVal[p.name];
        props.onChange(nextVal);
    };

    const clearFilter = (p: any) => {
        const nextVal: FilterSelections = { ...props.value };
        delete nextVal[p.name];
        props.onChange(nextVal);
    };

    const shownValues = keyword
        ? values.filter(v => (v.text ?? v.value ?? "").toLowerCase().includes(keyword.toLowerCase()))
        : values;

    const anySelected = Object.keys(props.value).some(n => (props.value[n]?.length ?? 0) > 0);

    return (
        <div className="filtering-mobile">

            {
                anySelected &&
                <FlexRow className="mb-3">
                    <button type="button" className="btn btn-sm btn-light-danger ms-auto py-1"
                        onClick={() => props.onChange({})}>
                        <Icon icon="xmark" className="me-1" />
                        Clear All
                    </button>
                </FlexRow>
            }

            {
                items.map((p, k) => {
                    const sel = selected(p.name);
                    return (
                        <button key={k} type="button" className="filtering-mobile-row" onClick={() => openFilter(p)}>
                            <span className="fm-icon"><Icon icon={p.icon} type="solid" size={14} /></span>
                            <span className="fm-title">{p.title}</span>
                            <span className={"fm-summary" + (sel.length ? " active" : "")}>{summary(p)}</span>
                            <Icon icon="chevron-down" size={12} className="ms-2 text-gray-500" />
                        </button>
                    );
                })
            }

            <BottomSheet id="filtering-mobile-sheet" open={!!activeProp} onClose={() => activePropSet(undefined)}>
                {
                    activeProp &&
                    <div className="filtering-mobile-sheet">

                        <FlexRow className="mb-3">
                            <div className="fs-4 fw-bolder text-gray-900">{activeProp.title}</div>
                            {
                                selected(activeProp.name).length > 0 &&
                                <button type="button" className="btn btn-sm btn-light-danger py-1 ms-auto"
                                    onClick={() => clearFilter(activeProp)}>
                                    Clear
                                </button>
                            }
                        </FlexRow>

                        {
                            activeProp.type !== "boolean" &&
                            <input type="text" className="form-control form-control-sm mb-3" placeholder="Search..."
                                value={keyword} onChange={e => keywordSet(e.target.value)} />
                        }

                        {/* Fixed-height scroll area so the sheet doesn't shrink/jump as results change. */}
                        <div className={"filtering-mobile-list" + (activeProp.type !== "boolean" ? " fixed" : "")}>
                            {
                                loading ?
                                    <div className="py-10"><Indicator /></div>
                                    :
                                    shownValues.length === 0 ?
                                        <div className="text-center text-gray-500 py-8 fs-7">No options.</div>
                                        :
                                        shownValues.map((v, k) => {
                                            const on = isSelected(activeProp.name, v.value);
                                            return (
                                                <div key={k} className={"filtering-mobile-value" + (on ? " on" : "")}
                                                    onClick={() => toggleValue(activeProp, v)}>
                                                    {
                                                        v.imageUrl ?
                                                            <Avatar avatarUrl={v.imageUrl} fullName={v.text} width={28} className="me-3" />
                                                            :
                                                            <span className="fm-val-icon">
                                                                <Icon icon={v.icon ?? activeProp.icon} type="solid" size={13} color={v.color} />
                                                            </span>
                                                    }
                                                    <div className="flex-grow-1">
                                                        <div className="fm-val-text">{v.text}</div>
                                                        {v.subText && <div className="fm-val-sub">{v.subText}</div>}
                                                    </div>
                                                    <Icon icon={on ? "square-check" : "square"} type={on ? "solid" : "regular"}
                                                        size={18} className={on ? "text-primary" : "text-gray-400"} />
                                                </div>
                                            );
                                        })
                            }
                        </div>
                    </div>
                }
            </BottomSheet>

        </div>
    );
}
